import { WellnessEntry, FoodEntry, FastingEntry, WalkingEntry, JournalEntry, HealthEntry, WaterEntry } from '../models/entry-types';
import { STORAGE_KEYS } from '../constants/storage-keys';
import { loadData, saveLocalData } from '../utils/local-storage';
import { clearCacheForKey, clearTrendCache } from '../utils/cache-utils';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { backgroundSync } from './background-sync-service';
import { eventBus, SYNC_EVENTS } from '../utils';

export const saveEntry = async (entry: WellnessEntry): Promise<void> => {
  try {
    console.log('saveEntry called with entry:', entry);
    
    // Check if the entry has all required fields
    if (!entry.id || !entry.type || !entry.date) {
      console.error('Entry missing required fields:', entry);
      toast.error('Entry is missing required fields');
      throw new Error('Entry is missing required fields');
    }

    // Save to local storage first for immediate UI feedback
    const key = getKeyForEntryType(entry.type);
    const entries = loadData<WellnessEntry[]>(key, []);
    
    const existingIndex = entries.findIndex(e => e.id === entry.id);
    
    if (existingIndex !== -1) {
      console.log(`Updating existing entry: ${entry.id}`);
      entries[existingIndex] = entry;
    } else {
      console.log(`Adding new entry: ${entry.id}`);
      entries.push(entry);
    }
    
    const cacheKey = `entries-${entry.date}`;
    clearCacheForKey(cacheKey);
    
    console.log(`Saving ${entries.length} entries to local storage ${key}`);
    
    // Save to local storage synchronously
    try {
      saveLocalData(key, entries);
      clearCacheForKey(key);
      clearTrendCache(entry.type);
      
      // Dispatch local update event immediately
      eventBus.dispatch(SYNC_EVENTS.LOCAL_UPDATE, { 
        type: entry.type, 
        date: entry.date 
      });
      
      // Check authentication status asynchronously
      checkAuthAndSaveToSupabase(entry).catch(error => {
        console.error('Background save to Supabase failed:', error);
        toast.error(`Saved locally, but syncing failed: ${error?.message || 'unknown error'}`);
      });
      
      // Show immediate success feedback for local save
      toast.success('Entry saved', { duration: 1800 });
      return Promise.resolve();
    } catch (error: any) {
      console.error('Error in local saveData:', error);
      toast.error('Could not save entry locally. Please try again.');
      throw error;
    }
  } catch (error) {
    console.error('Error in saveEntry service:', error);
    throw error;
  }
};

// Background function to check auth and save to Supabase
async function checkAuthAndSaveToSupabase(entry: WellnessEntry): Promise<void> {
  try {
    // Check authentication status
    const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError) {
      console.error('Session error:', sessionError);
      return Promise.reject(new Error(`Authentication error: ${sessionError.message}`));
    }
    
    const isAuthenticated = !!sessionData.session?.user;
    const userId = sessionData.session?.user?.id;
    
    if (!isAuthenticated || !userId) {
      console.warn('User not authenticated when saving entry');
      // No need to show error toast as data is saved locally
      return;
    }
    
    // Ensure entry has user_id
    entry.user_id = userId;
    
    // Save to Supabase with type-specific handling
    try {
      if (entry.type === 'food') {
        await saveSupabaseFood(entry as FoodEntry);
      } else if (entry.type === 'fasting') {
        await saveSupabaseFasting(entry as FastingEntry);
      } else if (entry.type === 'walking') {
        await saveSupabaseWalking(entry as WalkingEntry);
      } else if (entry.type === 'journal') {
        await saveSupabaseJournal(entry as JournalEntry);
      } else if (entry.type === 'health') {
        await saveSupabaseHealth(entry as HealthEntry);
      } else if (entry.type === 'water') {
        await saveSupabaseWater(entry as WaterEntry);
      }
      // All entry types are handled above
      
      console.log(`Entry ${entry.id} saved successfully to database`);
      
      // Schedule a background sync to ensure all data is in sync
      backgroundSync.scheduleSync('normal');
    } catch (error) {
      console.error('Supabase save error:', error);
      // Schedule higher priority sync to retry
      backgroundSync.scheduleSync('priority');
      throw error;
    }
  } catch (error) {
    console.error('Error in checkAuthAndSaveToSupabase:', error);
    throw error;
  }
}

async function saveSupabaseFood(foodEntry: FoodEntry): Promise<void> {
  // Map the client-side model to the database schema
  const dbEntry = {
    id: foodEntry.id,
    user_id: foodEntry.user_id,
    date: foodEntry.date,
    timestamp: foodEntry.timestamp,
    description: foodEntry.description || 'Unnamed food',
    meal_type: foodEntry.mealType,
    carbs: foodEntry.carbs || 0,
    protein: foodEntry.protein || 0,
    fat: foodEntry.fat || 0,
    fiber: foodEntry.fiber || 0,
    sugar: foodEntry.sugar || 0,
    calories: Math.round(Number(foodEntry.calories) || 0)
  };
  
  console.log('Saving to Supabase food_entries:', dbEntry);
  
  const { error: supabaseError } = await supabase
    .from('food_entries')
    .upsert(dbEntry, { 
      onConflict: 'id',
      ignoreDuplicates: false
    });
    
  if (supabaseError) {
    console.error('Supabase food save error:', supabaseError);
    throw supabaseError;
  }
}

async function saveSupabaseFasting(fastingEntry: FastingEntry): Promise<void> {
  // Map the client-side model to the database schema
  const dbEntry = {
    id: fastingEntry.id,
    user_id: fastingEntry.user_id,
    date: fastingEntry.date,
    timestamp: fastingEntry.timestamp,
    start_time: fastingEntry.startTime,
    end_time: fastingEntry.endTime,
    duration: fastingEntry.duration
  };
  
  console.log('Saving fasting entry to Supabase:', dbEntry);
  
  const { error: supabaseError } = await supabase
    .from('fasting_sessions')
    .upsert(dbEntry, { 
      onConflict: 'id',
      ignoreDuplicates: false
    });
    
  if (supabaseError) {
    console.error('Supabase fasting save error:', supabaseError);
    throw supabaseError;
  }
}

async function saveSupabaseWalking(walkingEntry: WalkingEntry): Promise<void> {
  // Map the client-side model to the database schema
  const dbEntry = {
    id: walkingEntry.id,
    user_id: walkingEntry.user_id,
    date: walkingEntry.date,
    timestamp: walkingEntry.timestamp,
    duration: walkingEntry.duration
  };
  
  console.log('Saving walking entry to Supabase:', dbEntry);
  
  const { error: supabaseError } = await supabase
    .from('walking_entries')
    .upsert(dbEntry, { 
      onConflict: 'id',
      ignoreDuplicates: false
    });
    
  if (supabaseError) {
    console.error('Supabase walking save error:', supabaseError);
    throw supabaseError;
  }
}

async function saveSupabaseJournal(journalEntry: JournalEntry): Promise<void> {
  // Map the client-side model to the database schema
  const dbEntry = {
    id: journalEntry.id,
    user_id: journalEntry.user_id,
    date: journalEntry.date,
    timestamp: journalEntry.timestamp,
    content: journalEntry.content
  };
  
  console.log('Saving journal entry to Supabase:', dbEntry);
  
  const { error: supabaseError } = await supabase
    .from('journal_entries')
    .upsert(dbEntry, { 
      onConflict: 'id',
      ignoreDuplicates: false
    });
    
  if (supabaseError) {
    console.error('Supabase journal save error:', supabaseError);
    throw supabaseError;
  }
}

async function saveSupabaseHealth(healthEntry: HealthEntry): Promise<void> {
  // Map the client-side model to the database schema
  const dbEntry = {
    id: healthEntry.id,
    user_id: healthEntry.user_id,
    date: healthEntry.date,
    timestamp: healthEntry.timestamp,
    weight: healthEntry.weight,
    sleep_hours: healthEntry.sleep
  };
  
  console.log('Saving health entry to Supabase:', dbEntry);
  
  const { error: supabaseError } = await supabase
    .from('health_entries')
    .upsert(dbEntry, { 
      onConflict: 'id',
      ignoreDuplicates: false
    });
    
  if (supabaseError) {
    console.error('Supabase health save error:', supabaseError);
    throw supabaseError;
  }
}

async function saveSupabaseWater(waterEntry: WaterEntry): Promise<void> {
  // Map the client-side model to the database schema
  const dbEntry = {
    id: waterEntry.id,
    user_id: waterEntry.user_id,
    date: waterEntry.date,
    timestamp: waterEntry.timestamp,
    amount: waterEntry.amount
  };
  
  console.log('Saving water entry to Supabase:', dbEntry);
  
  const { error: supabaseError } = await supabase
    .from('water_entries')
    .upsert(dbEntry, { 
      onConflict: 'id',
      ignoreDuplicates: false
    });
    
  if (supabaseError) {
    console.error('Supabase water save error:', supabaseError);
    throw supabaseError;
  }
}

export const deleteEntry = async (entryId: string, entryType: string): Promise<void> => {
  try {
    if (!entryId || !entryType) {
      throw new Error('Entry ID and type are required');
    }
    
    // Check auth state first
    const { data: sessionData } = await supabase.auth.getSession();
    const isAuthenticated = !!sessionData.session?.user;
    
    if (!isAuthenticated) {
      console.warn('User not authenticated when deleting entry');
      toast.error('Please sign in to delete entries');
      return Promise.reject(new Error('Authentication required'));
    }
    
    // Delete from Supabase first
    const tableMap: Record<string, string> = {
      food: 'food_entries',
      walking: 'walking_entries',
      journal: 'journal_entries',
      health: 'health_entries',
      water: 'water_entries',
      fasting: 'fasting_sessions',
    };
    const table = tableMap[entryType];
    if (table) {
      const { error: deleteError } = await supabase
        .from(table as any)
        .delete()
        .eq('id', entryId);
        
      if (deleteError) {
        console.error('Error deleting from Supabase:', deleteError);
        throw deleteError;
      }
    }
    
    // Then delete from local storage as backup
    const key = getKeyForEntryType(entryType);
    const entries = loadData<WellnessEntry[]>(key, []);
    const filteredEntries = entries.filter(e => e.id !== entryId);
    
    console.log(`Deleting entry ${entryId}, ${entries.length - filteredEntries.length} entries removed`);
    
    saveLocalData(key, filteredEntries);
    clearCacheForKey(key);
    console.log(`Successfully deleted entry: ${entryId}`);
    toast.success('Entry deleted');
  } catch (error) {
    console.error('Error deleting entry:', error);
    toast.error('Failed to delete entry');
    throw error;
  }
};

function getKeyForEntryType(type: string): string {
  if (!type) {
    console.error('Invalid entry type provided:', type);
    toast.error('Invalid entry type');
    throw new Error('Invalid entry type');
  }
  
  switch (type) {
    case 'fasting':
      return STORAGE_KEYS.FASTING_SESSIONS;
    case 'walking':
      return STORAGE_KEYS.WALK_ENTRIES;
    case 'journal':
      return STORAGE_KEYS.JOURNAL_ENTRIES;
    case 'food':
      return STORAGE_KEYS.FOOD_ENTRIES;
    case 'health':
      return STORAGE_KEYS.HEALTH_ENTRIES;
    case 'water':
      return STORAGE_KEYS.WATER_ENTRIES;
    default:
      const errorMsg = `Unknown entry type: ${type}`;
      console.error(errorMsg);
      toast.error('Invalid entry type');
      throw new Error(errorMsg);
  }
}
